import React from "react";
import { Link } from "react-router-dom";

export type EntityProfileField = {
  label: string;
  value?: React.ReactNode;
};

export type EntityProfileViewCardProps = {
  name: string;
  subtitle: string;
  status?: string | null;
  editHref?: string;
  avatarUrl?: string;
  initials: string;
  fields: EntityProfileField[];
};

function isEmptyValue(value: React.ReactNode): boolean {
  if (value === null || value === undefined || value === false) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return !trimmed || trimmed === "---" || trimmed === "Not provided";
  }
  return false;
}

function formatStatusLabel(status?: string | null): string {
  const raw = String(status ?? "").trim();
  if (!raw) return "";
  if (/^true$/i.test(raw)) return "Active";
  if (/^false$/i.test(raw)) return "Inactive";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function ProfileEditIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

export default function EntityProfileViewCard({
  name,
  subtitle,
  status,
  editHref,
  avatarUrl,
  initials,
  fields,
}: EntityProfileViewCardProps) {
  const statusLabel = formatStatusLabel(status);
  const isActive = /^(active|true)$/i.test(String(status ?? ""));
  const hasFields = fields.length > 0;

  return (
    <div className="re-profile-card">
      <div className={`re-profile-card__top${hasFields ? "" : " re-profile-card__top--no-fields"}`}>
        <div className={`re-profile-card__avatar${avatarUrl ? " re-profile-card__avatar--image" : ""}`}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name || "Profile"}
              loading="lazy"
            />
          ) : (
            initials
          )}
        </div>

        <div className="re-profile-card__id">
          <div className="re-profile-card__name-row">
            <span className="re-profile-card__name">{name || "—"}</span>
            {statusLabel ? (
              <span className="re-profile-card__status">
                <span className={`re-profile-card__dot${isActive ? "" : " re-profile-card__dot--inactive"}`} />
                {statusLabel}
              </span>
            ) : null}
          </div>
          <div className="re-profile-card__subtitle">{subtitle}</div>
        </div>

        {editHref ? (
          <Link to={editHref} className="re-profile-card__edit-btn" title="Edit">
            <ProfileEditIcon />
          </Link>
        ) : null}
      </div>

      {hasFields ? (
        <div className="re-profile-card__fields">
          {fields.map(({ label, value }) => (
            <div key={label}>
              <div className="re-profile-card__label">{label}</div>
              {isEmptyValue(value) ? (
                <div className="re-profile-card__value empty">—</div>
              ) : (
                <div className="re-profile-card__value">{value}</div>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
